<template>
  <div class="vanilla-d-wrap">
    <main class="screen" role="main">

      <div class="header">
        <button
          type="button"
          class="back-button"
          @click="goBack"
          :disabled="currentIndex === 0"
          aria-label="Zurück"
        >←</button>
        <div class="header-actions">
          <button
            type="button"
            class="primer-button"
            aria-haspopup="dialog"
            @click="primerOpen = true"
          >
            <span class="primer-icon" aria-hidden="true">i</span>
            Was ist eine Portion?
          </button>
        </div>
      </div>

      <div class="counter-row">
        <span class="question-counter">
          Frage {{ currentIndex + 1 }} von {{ questions.length }}
        </span>
      </div>

      <div
        class="progress-bar"
        role="progressbar"
        :aria-valuenow="currentIndex + 1"
        aria-valuemin="1"
        :aria-valuemax="questions.length"
        aria-label="Fortschritt"
      >
        <div
          v-for="(seg, i) in progressSegments"
          :key="`seg-${i}`"
          class="progress-segment"
        >
          <div
            v-if="seg.fill > 0"
            class="progress-segment-fill"
            :style="{ width: (seg.fill * 100) + '%' }"
          ></div>
        </div>
      </div>

      <div class="question-block">
        <p class="section-label">{{ currentSectionLabel }}</p>
        <h1 class="question-title">{{ currentQuestion.title }}</h1>
        <p class="question-prompt">{{ currentQuestion.prompt }}</p>
      </div>

      <div class="stepper">
        <button
          type="button"
          class="stepper-button"
          :disabled="currentValue <= currentQuestion.min"
          aria-label="Weniger"
          @click="decrement"
        >−</button>
        <div class="stepper-value">
          <span class="stepper-number">{{ currentValue }}</span>
          <span class="stepper-unit">{{ unitLabel }}</span>
        </div>
        <button
          type="button"
          class="stepper-button"
          :disabled="currentValue >= currentQuestion.max"
          aria-label="Mehr"
          @click="increment"
        >+</button>
      </div>

      <p class="helper-text">Tippe + oder − um deine Antwort anzupassen.</p>

      <div class="footer">
        <button type="button" class="next-button" @click="goNext">
          {{ isLast ? 'Fertig' : 'Weiter' }}
        </button>
      </div>

      <div
        v-if="primerOpen"
        class="primer-overlay"
        role="dialog"
        aria-labelledby="primer-title"
        @click.self="primerOpen = false"
      >
        <div class="primer-sheet">
          <div class="primer-sheet-header">
            <h2 class="primer-sheet-title" id="primer-title">Was ist eine Portion?</h2>
            <button
              type="button"
              class="primer-close"
              aria-label="Schliessen"
              @click="primerOpen = false"
            >×</button>
          </div>
          <ul class="portion-list">
            <li v-for="(entry, i) in portionPrimer" :key="i">
              <span class="portion-amount">{{ entry.amount }}</span>
              <span class="portion-item">
                {{ entry.item }}
                <span class="portion-note">{{ entry.note }}</span>
              </span>
            </li>
          </ul>
        </div>
      </div>

    </main>
  </div>
</template>

<script>
import { sections, portionQuestions, portionPrimer } from '../_data/sampleQuestions.js';

export default {
  name: 'VanillaD',
  data() {
    return {
      questions: portionQuestions,
      portionPrimer,
      currentIndex: 0,
      primerOpen: false,
      answers: Object.fromEntries(portionQuestions.map(q => [q.id, q.initial])),
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex];
    },
    currentValue() {
      return this.answers[this.currentQuestion.id];
    },
    currentSectionLabel() {
      const section = sections.find(s => s.id === this.currentQuestion.sectionId);
      return section ? section.label : '';
    },
    unitLabel() {
      return this.currentValue === 1
        ? this.currentQuestion.unit
        : this.currentQuestion.unitPlural;
    },
    isLast() {
      return this.currentIndex === this.questions.length - 1;
    },
    progressSegments() {
      return this.questions.map((_, i) => ({
        fill: i < this.currentIndex ? 1 : i === this.currentIndex ? 1 : 0,
      }));
    },
  },
  methods: {
    increment() {
      if (this.currentValue < this.currentQuestion.max) {
        this.answers = { ...this.answers, [this.currentQuestion.id]: this.currentValue + 1 };
      }
    },
    decrement() {
      if (this.currentValue > this.currentQuestion.min) {
        this.answers = { ...this.answers, [this.currentQuestion.id]: this.currentValue - 1 };
      }
    },
    goBack() {
      if (this.currentIndex === 0) return;
      this.currentIndex--;
      this.primerOpen = false;
    },
    goNext() {
      if (this.isLast) {
        this.$router.push('/postsurvey/email');
        return;
      }
      this.currentIndex++;
      this.primerOpen = false;
    },
  },
};
</script>

<style scoped>
.vanilla-d-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.vanilla-d-wrap *,
.vanilla-d-wrap *::before,
.vanilla-d-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1rem 1.25rem;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  position: relative;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 20px;
  color: var(--color-text-secondary);
  line-height: 1;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.primer-button {
  background: var(--color-accent-light);
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: 6px 10px;
  font-size: 12px;
  color: var(--color-accent);
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.primer-button:hover {
  background: var(--color-accent);
  color: white;
}

.primer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-accent);
  color: white;
  font-size: 10px;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
}

.primer-button:hover .primer-icon {
  background: white;
  color: var(--color-accent);
}

.counter-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.question-counter {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 1.5rem;
}

.progress-segment {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--color-bg-muted);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: 3px;
  transition: width 0.25s ease;
}

.question-block {
  margin-bottom: 1.5rem;
}

.section-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 6px;
}

.question-title {
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 8px;
  line-height: 1.25;
}

.question-prompt {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 2rem 1rem;
  background: var(--color-bg-muted);
  border-radius: var(--radius-lg);
  margin-bottom: 1.5rem;
}

.stepper-button {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 0.5px solid var(--color-border);
  background: var(--color-bg-card);
  font-size: 28px;
  font-weight: 400;
  color: var(--color-text-primary);
  cursor: pointer;
  font-family: inherit;
  line-height: 1;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.stepper-button:hover:not(:disabled) {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.stepper-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.stepper-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.stepper-number {
  font-size: 48px;
  font-weight: 500;
  color: var(--color-text-primary);
  line-height: 1;
}

.stepper-unit {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-top: 6px;
}

.helper-text {
  text-align: center;
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.footer {
  margin-top: auto;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-accent);
  color: white;
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
}

.next-button:hover {
  opacity: 0.9;
}

.primer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 10;
}

.primer-sheet {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  width: 100%;
  padding: 1.5rem 1.25rem;
  max-height: 80%;
  overflow-y: auto;
}

.primer-sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 12px;
}

.primer-sheet-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  line-height: 1.3;
}

.primer-close {
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--color-text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.portion-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.portion-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 0.5px solid var(--color-border);
}

.portion-list li:last-child {
  border-bottom: none;
}

.portion-amount {
  flex-shrink: 0;
  font-weight: 500;
  color: var(--color-accent);
  font-size: 14px;
  min-width: 70px;
}

.portion-item {
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.4;
}

.portion-note {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-top: 2px;
  display: block;
}
</style>
